import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {FaIcon} from '../FaIcon';
import {LocalStorage} from '../../utils/LocalStorage';
import {PORTAL_LANGUAGES, currentPortalLanguage, saveUserLanguage} from './portalLanguage';

const PANEL_ID = 'portal-language-options';

/**
 * El idioma del usuario del portal (pedido de Fran, iVirtual, wa#220). En la barra es un botón que
 * despliega las opciones; en el menú del celular (`variant="mobile"`), la lista a la vista.
 *
 * Es un desplegable simple (botón con `aria-expanded` y un grupo de botones con `aria-pressed`), no
 * un `role="menu"`: ese patrón pide flechas y manejo de foco que acá no hacen falta para dos
 * opciones (review C-portal-idioma-08). Lo que sí se cuida: cerrar con Escape o al elegir devuelve el
 * foco al botón de la barra, para que no caiga al `<body>` cuando el desplegable se desmonta.
 *
 * Guarda primero en el backend y recién después cambia la pantalla: si el guardado falla, el
 * portal sigue en el idioma que tenía y lo dice. `onChanged` le avisa a `LayoutPortal`, que remonta
 * el contenido para que todo quede en el idioma nuevo (hay celdas del Tablero que traducen con la
 * instancia de i18next y no se enteran solas del cambio).
 */
export const PortalLanguageMenu = ({variant = 'topbar', onChanged}) => {
    const {t, i18n} = useTranslation();
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);
    const current = currentPortalLanguage(i18n);

    const closeAndFocusTrigger = () => {
        setOpen(false);
        if (triggerRef.current) triggerRef.current.focus();
    };

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
        };
        // Con el ref y el setter, que son estables, y no con `closeAndFocusTrigger`: si no, el efecto
        // dependería de una función nueva en cada render.
        const handleKeyDown = (event) => {
            if (event.key !== 'Escape') return;
            setOpen(false);
            if (triggerRef.current) triggerRef.current.focus();
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    const choose = async (code) => {
        if (variant === 'mobile') setOpen(false); else closeAndFocusTrigger();
        if (code === current || saving) return;
        setSaving(true);
        const saved = await saveUserLanguage(code);
        setSaving(false);
        if (!saved) {
            toast.error(t('portal.language.error'));
            return;
        }
        LocalStorage.updateUserLanguage(code);
        await i18n.changeLanguage(code);
        // Con `i18n.t` y no con el `t` del render: el aviso tiene que salir en el idioma nuevo.
        toast.success(i18n.t('portal.language.changed'));
        if (onChanged) onChanged(code);
    };

    const options = PORTAL_LANGUAGES.map((option) => {
        const isCurrent = option.code === current;
        return (
            <button key={option.code}
                    type="button"
                    aria-pressed={isCurrent}
                    lang={option.code}
                    disabled={saving}
                    className={`${variant === 'mobile' ? 'portal-nav-mobile-item' : 'portal-language-menu__item'} ${isCurrent ? '-active' : ''}`}
                    onClick={() => choose(option.code)}
                    data-testid={`portal-language-${option.code}`}>
                {isCurrent
                    ? <FaIcon iconName="check"/>
                    : <span className="portal-language-menu__spacer" aria-hidden="true"/>}
                <span>{option.label}</span>
            </button>
        );
    });

    if (variant === 'mobile') {
        return (
            <div className="portal-nav-mobile-language" role="group" aria-label={t('portal.language.label')}>
                <div className="portal-nav-mobile-language__label">
                    <FaIcon iconName="globe"/>
                    <span>{t('portal.language.label')}</span>
                </div>
                {options}
            </div>
        );
    }

    return (
        <div className="portal-language-menu" ref={wrapperRef}>
            {/* Mientras guarda, ocupado pero no `disabled`: el foco vuelve a este botón al elegir, y
                deshabilitarlo con el foco encima lo perdería. */}
            <button type="button"
                    ref={triggerRef}
                    className="portal-language-menu__trigger"
                    onClick={() => { if (!saving) setOpen(!open); }}
                    aria-busy={saving}
                    aria-expanded={open}
                    aria-controls={PANEL_ID}
                    aria-label={t('portal.language.label')}
                    data-testid="portal-language-trigger">
                <FaIcon iconName="globe"/>
                <span className="portal-language-menu__code">{current.toUpperCase()}</span>
            </button>
            {open && (
                <div id={PANEL_ID} className="portal-language-menu__menu" role="group" aria-label={t('portal.language.label')}>
                    <div className="portal-language-menu__title" aria-hidden="true">{t('portal.language.label')}</div>
                    {options}
                </div>
            )}
        </div>
    );
};
